import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTimes,
  FaCommentDots,
  FaUser,
  FaCalendar,
  FaPhone,
  FaEnvelope,
  FaTag,
  FaDownload,
  FaEdit,
  FaTrash,
  FaCheck,
} from "react-icons/fa";
import { BsFillChatLeftQuoteFill } from "react-icons/bs";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import "react-quill/dist/quill.snow.css";
import "utils/quillImageFormat"; // 이미지 width/height/style 보존 (재편집 시 크기 유지)
import CommonModal from "./CommonModal";
import { catchError, commonModalSetting } from "JS/common";
import {
  getCsReviewDetail,
  getFileDownload,
  editReview,
  postCsReviewAnswer,
  putCsReviewAnswer,
  deleteCsReviewAnswer,
} from "JS/API";
import { designTokens, commonStyles, badgeStyles } from "utils/designSystem";
import { detailModalStyles } from "utils/modalDesignSystem";

Quill.register("modules/imageResize", ImageResize);

const REVIEW_TYPES = ["출원후기", "등록가능성검토 후기"];
const ANSWER_MAX = 500;

const CsReviewDetail = ({ review, setModal }) => {
  let prevent = false;
  const reviewId = review?.id;

  const [alert, setAlert] = useState("");
  const [edit, setEdit] = useState(false); // 본문 수정 모드
  const [answerEdit, setAnswerEdit] = useState(false); // 답글 수정 모드
  const [answerText, setAnswerText] = useState("");
  const [info, setInfo] = useState({
    review_type: review?.review_type || "",
    title: review?.title || "",
    author_name: review?.author_name || "",
    is_member: review?.is_member ?? true,
    is_admin: review?.is_admin || false,
    created_at: review?.created_at || "",
    answered_at: review?.answered_at || "",
    guest_name: review?.guest_name || "",
    guest_phone: review?.guest_phone || "",
    guest_email: review?.guest_email || "",
    context: "",
    answer: "",
    admin_name: "",
    upload_filelist: [],
  });
  const [editForm, setEditForm] = useState({
    review_type: review?.review_type || "",
    title: review?.title || "",
    author_name: review?.author_name || "",
    is_member: review?.is_member ?? true,
    context: "",
  });
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      ["image"],
      [{ align: [] }, { color: [] }, { background: [] }],
      ["clean"],
    ],
    imageResize: {
      parchment: Quill.import("parchment"),
      modules: ["Resize", "DisplaySize"],
    },
  };
  const quillFormats = [
    "header", "bold", "italic", "underline", "strike", "blockquote",
    "link", "image", "align", "color", "background",
    "width", "height", "style", "alt", // 이미지 리사이즈 속성 보존
  ];

  const hasAnswer = !!info.answer;

  // 스타일 정의
  const modalStyles = {
    overlay: detailModalStyles.overlay,
    container: {
      backgroundColor: designTokens.colors.surface,
      borderRadius: designTokens.borderRadius.xl,
      boxShadow: designTokens.shadows.lg,
      width: '100%', maxWidth: '1000px', maxHeight: '90vh',
      display: 'flex', flexDirection: 'column', overflow: 'hidden'
    },
    header: detailModalStyles.header,
    title: detailModalStyles.title,
    closeButton: {
      ...commonStyles.buttonBase,
      padding: designTokens.spacing.md, borderRadius: '50%',
      backgroundColor: 'transparent', color: designTokens.colors.text.tertiary,
      cursor: 'pointer', border: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '36px', height: '36px'
    },
    content: detailModalStyles.content,
    statusHeader: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: designTokens.spacing.xl, padding: designTokens.spacing.lg,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.lg,
      border: `1px solid ${designTokens.colors.border}`
    },
    dateInfo: {
      display: 'flex', alignItems: 'center', gap: designTokens.spacing.sm,
      fontSize: '14px', color: designTokens.colors.text.secondary
    },
    memberBadge: {
      ...badgeStyles.base, fontSize: '12px', fontWeight: '600'
    },
    section: detailModalStyles.section,
    sectionTitle: {
      display: 'flex', alignItems: 'center', gap: designTokens.spacing.md,
      fontSize: '16px', fontWeight: '600',
      color: designTokens.colors.text.primary,
      marginBottom: designTokens.spacing.lg,
      padding: `${designTokens.spacing.md} 0`,
      borderBottom: `2px solid ${designTokens.colors.primary}`
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: designTokens.spacing.lg
    },
    infoRow: {
      display: 'flex', alignItems: 'center', padding: designTokens.spacing.md,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`
    },
    infoLabel: {
      display: 'flex', alignItems: 'center', gap: designTokens.spacing.sm,
      fontSize: '14px', fontWeight: '600',
      color: designTokens.colors.text.secondary, minWidth: '120px'
    },
    infoValue: {
      fontSize: '14px', color: designTokens.colors.text.primary,
      fontWeight: '500', flex: 1, wordBreak: 'break-all'
    },
    questionHeader: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: designTokens.spacing.lg
    },
    questionTitle: {
      fontSize: '16px', fontWeight: '600',
      color: designTokens.colors.text.primary
    },
    downloadButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.success,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      fontSize: '12px', marginRight: designTokens.spacing.sm,
      marginBottom: designTokens.spacing.sm
    },
    htmlContent: {
      padding: designTokens.spacing.lg,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.lg,
      border: `1px solid ${designTokens.colors.borderLight}`,
      lineHeight: '1.6', fontSize: '14px',
      color: designTokens.colors.text.primary,
      minHeight: '100px', overflowWrap: 'break-word'
    },
    input: {
      width: '100%', padding: designTokens.spacing.md,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: '14px', fontFamily: 'inherit',
      marginBottom: designTokens.spacing.md, boxSizing: 'border-box'
    },
    answerTextarea: {
      width: '100%', minHeight: '160px', maxHeight: '400px',
      padding: designTokens.spacing.md,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.lg,
      fontSize: '14px', lineHeight: '1.6', resize: 'vertical',
      fontFamily: 'inherit', boxSizing: 'border-box'
    },
    answerContent: {
      padding: designTokens.spacing.lg,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.lg,
      border: `1px solid ${designTokens.colors.borderLight}`,
      lineHeight: '1.6', fontSize: '14px',
      color: designTokens.colors.text.primary,
      maxHeight: '400px', overflow: 'auto', whiteSpace: 'pre-wrap'
    },
    footer: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginTop: designTokens.spacing.lg, padding: designTokens.spacing.lg,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.lg, flexWrap: 'wrap',
      gap: designTokens.spacing.md
    },
    byteCount: {
      fontSize: '12px', color: designTokens.colors.text.tertiary,
      display: 'flex', alignItems: 'center', gap: designTokens.spacing.xs
    },
    buttonGroup: {
      display: 'flex', gap: designTokens.spacing.md, flexWrap: 'wrap'
    },
    primaryButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
      fontSize: '14px', fontWeight: '600'
    },
    secondaryButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.surface,
      color: designTokens.colors.secondary,
      border: `1px solid ${designTokens.colors.border}`,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
      fontSize: '14px', fontWeight: '600'
    },
    dangerButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.danger,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
      fontSize: '14px', fontWeight: '600'
    }
  };

  //= 후기 상세 조회 (공개 엔드포인트, context/answer/첨부)
  const getDetail = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);
    const result = await getCsReviewDetail(reviewId);
    if (typeof result === "object") {
      const d = result?.data || {};
      setInfo((prev) => ({
        ...prev,
        // 상세 응답에 값이 있으면 갱신, 없으면 목록 행 값 유지
        review_type: d.review_type ?? prev.review_type,
        title: d.title ?? prev.title,
        author_name: d.author_name ?? prev.author_name,
        is_member: d.is_member ?? prev.is_member,
        is_admin: d.is_admin ?? prev.is_admin,
        created_at: d.created_at ?? prev.created_at,
        answered_at: d.answered_at ?? prev.answered_at,
        guest_name: d.guest_name ?? prev.guest_name,
        guest_phone: d.phone ?? d.guest_phone ?? prev.guest_phone,
        guest_email: d.email ?? d.guest_email ?? prev.guest_email,
        context: d.context || "",
        answer: d.answer || "",
        admin_name: d.admin_name || "",
        upload_filelist: d.upload_filelist || [],
      }));
      setAnswerText(d.answer || "");
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  useEffect(() => {
    getDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //= 본문 수정 진입
  const startEdit = () => {
    setEditForm({
      review_type: info.review_type,
      title: info.title,
      author_name: info.author_name,
      is_member: info.is_member,
      context: info.context,
    });
    setEdit(true);
  };

  //= 본문 수정 저장
  const saveEdit = async () => {
    // 이미지 리사이즈는 onChange를 발생시키지 않으므로 에디터 DOM에서 직접 읽는다.
    const editorHtml = quillRef.current
      ? quillRef.current.getEditor().root.innerHTML
      : editForm.context;

    if (!editForm.title.trim())
      return commonModalSetting(setAlertBox, true, "alert", "제목을 입력해 주세요.");
    const plain = editorHtml.replace(/<[^>]*>/g, "").trim();
    const hasImage = /<img/i.test(editorHtml);
    if (!plain && !hasImage)
      return commonModalSetting(setAlertBox, true, "alert", "내용을 입력해 주세요.");

    const body = {
      review_id: reviewId,
      review_type: editForm.review_type,
      title: editForm.title.trim(),
      context: editorHtml,
      author_name: editForm.author_name.trim(),
      is_member: editForm.is_member,
    };
    const result = await editReview(body);
    if (typeof result === "object") {
      setEdit(false);
      setAlert("editComplete");
      commonModalSetting(setAlertBox, true, "alert", "후기가 수정되었습니다.");
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 답글 등록
  const submitAnswer = async () => {
    if (!answerText.trim())
      return commonModalSetting(setAlertBox, true, "alert", "답글을 작성해 주세요.");
    const result = await postCsReviewAnswer(reviewId, answerText);
    if (typeof result === "object") {
      setAlert("answerComplete");
      commonModalSetting(setAlertBox, true, "alert", "답글이 등록되었습니다.");
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 답글 수정
  const updateAnswer = async () => {
    if (!answerText.trim())
      return commonModalSetting(setAlertBox, true, "alert", "답글을 작성해 주세요.");
    const result = await putCsReviewAnswer(reviewId, answerText);
    if (typeof result === "object") {
      setAnswerEdit(false);
      setAlert("answerComplete");
      commonModalSetting(setAlertBox, true, "alert", "답글이 수정되었습니다.");
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 답글 삭제
  const removeAnswer = async () => {
    const result = await deleteCsReviewAnswer(reviewId);
    if (typeof result === "object") {
      setAnswerEdit(false);
      setAlert("answerDeleteComplete");
      commonModalSetting(setAlertBox, true, "alert", "답글이 삭제되었습니다.");
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 첨부 파일 다운로드
  const handleFileDownload = async (file) => {
    if (!file?.original_file_name) {
      window.alert("다운로드할 파일이 없습니다.");
      return;
    }
    try {
      const response = await getFileDownload(
        file.file_name,
        file.original_file_name
      );
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.original_file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("파일 다운로드 실패:", error);
      window.alert("파일 다운로드에 실패했습니다. 다시 시도해주세요.");
    }
  };

  return (
    <>
      <div style={modalStyles.overlay}>
        <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div style={modalStyles.header}>
            <h2 style={modalStyles.title}>
              <FaCommentDots />
              고객 후기 상세
              {info.is_admin && (
                <span
                  style={{
                    display: "inline-flex", alignItems: "center",
                    padding: "2px 10px", borderRadius: "4px",
                    fontSize: "12px", fontWeight: "600",
                    backgroundColor: "#fee2e2", color: "#b91c1c",
                    marginLeft: "8px",
                  }}
                >
                  운영글
                </span>
              )}
            </h2>
            <button
              style={modalStyles.closeButton}
              onClick={() => setModal(false)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.background;
                e.target.style.color = designTokens.colors.text.primary;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = designTokens.colors.text.tertiary;
              }}
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* 컨텐츠 */}
          <div style={modalStyles.content}>
            {/* 상태 헤더 */}
            <div style={modalStyles.statusHeader}>
              <div style={modalStyles.dateInfo}>
                <FaCalendar size={12} />
                작성일: {info.created_at ? info.created_at.replace("T", " ") : "-"}
              </div>
              <div style={{
                ...modalStyles.memberBadge,
                ...(info.is_member ? badgeStyles.primary : badgeStyles.neutral)
              }}>
                <FaUser size={10} />
                {info.is_member ? "회원" : "비회원"}
              </div>
            </div>

            {/* 작성자 정보 */}
            <div style={modalStyles.section}>
              <h3 style={modalStyles.sectionTitle}>
                <FaUser />
                작성자 정보
              </h3>
              <div style={modalStyles.infoGrid}>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaUser size={12} />
                    작성자
                  </div>
                  <div style={modalStyles.infoValue}>{info.author_name || "-"}</div>
                </div>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaTag size={12} />
                    후기 유형
                  </div>
                  <div style={modalStyles.infoValue}>{info.review_type || "-"}</div>
                </div>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaPhone size={12} />
                    연락처
                  </div>
                  <div style={modalStyles.infoValue}>{info.guest_phone || "-"}</div>
                </div>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaEnvelope size={12} />
                    이메일
                  </div>
                  <div style={modalStyles.infoValue}>{info.guest_email || "-"}</div>
                </div>
              </div>
            </div>

            {/* 후기 내용 */}
            <div style={modalStyles.section}>
              <div style={modalStyles.questionHeader}>
                <div style={modalStyles.questionTitle}>
                  {edit ? "후기 수정" : `제목: ${info.title}`}
                </div>
                {!edit && (
                  <button
                    style={modalStyles.primaryButton}
                    onClick={startEdit}
                  >
                    <FaEdit size={12} />
                    본문 수정
                  </button>
                )}
              </div>

              {edit ? (
                // 본문 수정 모드
                <>
                  <select
                    style={modalStyles.input}
                    value={editForm.review_type}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, review_type: e.target.value }))
                    }
                  >
                    {REVIEW_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <input
                    style={modalStyles.input}
                    type="text"
                    maxLength={300}
                    placeholder="제목"
                    value={editForm.title}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                  <input
                    style={modalStyles.input}
                    type="text"
                    maxLength={20}
                    placeholder="작성자명"
                    value={editForm.author_name}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, author_name: e.target.value }))
                    }
                  />
                  <div style={{ marginBottom: designTokens.spacing.md }}>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={editForm.context}
                      onChange={(v) => setEditForm((p) => ({ ...p, context: v }))}
                      modules={quillModules}
                      formats={quillFormats}
                    />
                  </div>
                  <div style={modalStyles.buttonGroup}>
                    <button style={modalStyles.primaryButton} onClick={saveEdit}>
                      <FaCheck size={12} />
                      저장
                    </button>
                    <button
                      style={modalStyles.secondaryButton}
                      onClick={() => setEdit(false)}
                    >
                      취소
                    </button>
                  </div>
                </>
              ) : (
                // 본문 조회 (HTML)
                <>
                  {info.upload_filelist?.length > 0 && (
                    <div style={{ marginBottom: designTokens.spacing.md }}>
                      {info.upload_filelist.map((file, idx) => (
                        <button
                          key={idx}
                          style={modalStyles.downloadButton}
                          onClick={() => handleFileDownload(file)}
                        >
                          <FaDownload size={10} />
                          {file.original_file_name || `첨부파일 ${idx + 1}`}
                        </button>
                      ))}
                    </div>
                  )}
                  <div
                    style={modalStyles.htmlContent}
                    dangerouslySetInnerHTML={{ __html: info.context }}
                  />
                </>
              )}
            </div>

            {/* 답글 섹션 */}
            <div style={modalStyles.section}>
              <h3 style={modalStyles.sectionTitle}>
                <BsFillChatLeftQuoteFill />
                {hasAnswer ? "답글" : "답글 작성"}
              </h3>

              {hasAnswer && !answerEdit ? (
                // 답글 있음 - 조회 (평문)
                <>
                  <div style={modalStyles.answerContent}>
                    {info.answer.split("\n").map((line, idx) => (
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                  <div style={modalStyles.footer}>
                    <div style={modalStyles.byteCount}>
                      {info.admin_name && <span>작성 관리자: {info.admin_name}</span>}
                      {info.answered_at && (
                        <span style={{ marginLeft: 8 }}>
                          {info.answered_at.replace("T", " ")}
                        </span>
                      )}
                    </div>
                    <div style={modalStyles.buttonGroup}>
                      <button
                        style={modalStyles.primaryButton}
                        onClick={() => {
                          setAnswerText(info.answer);
                          setAnswerEdit(true);
                        }}
                      >
                        <FaEdit size={12} />
                        답글 수정
                      </button>
                      <button
                        style={modalStyles.dangerButton}
                        onClick={() => {
                          setAlert("answerDelete");
                          commonModalSetting(
                            setAlertBox, true, "confirm",
                            "답글을 삭제하시면 다시 복구할 수 없습니다.<br/>답글을 삭제하시겠습니까?"
                          );
                        }}
                      >
                        <FaTrash size={12} />
                        답글 삭제
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // 답글 없음(등록) 또는 수정
                <>
                  <textarea
                    style={modalStyles.answerTextarea}
                    value={answerText}
                    maxLength={ANSWER_MAX}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="답글을 작성해주세요. (평문, 최대 500자)"
                  />
                  <div style={modalStyles.footer}>
                    <div style={modalStyles.byteCount}>
                      <span>{answerText.length} / {ANSWER_MAX}자</span>
                    </div>
                    <div style={modalStyles.buttonGroup}>
                      {answerEdit ? (
                        <>
                          <button
                            style={modalStyles.primaryButton}
                            onClick={updateAnswer}
                          >
                            <FaCheck size={12} />
                            수정 완료
                          </button>
                          <button
                            style={modalStyles.secondaryButton}
                            onClick={() => {
                              setAnswerEdit(false);
                              setAnswerText(info.answer);
                            }}
                          >
                            취소
                          </button>
                        </>
                      ) : (
                        <button
                          style={modalStyles.primaryButton}
                          onClick={submitAnswer}
                        >
                          <FaCheck size={12} />
                          답글 등록
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
            else if (alert === "answerDelete") removeAnswer();
            else if (
              alert === "answerComplete" ||
              alert === "answerDeleteComplete" ||
              alert === "editComplete"
            )
              getDetail();
          }}
        />
      )}
    </>
  );
};

export default CsReviewDetail;
